import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
} from "react";
import "./NewHomePage.css";
import { CircularProgress, Box } from "@mui/material";
import BannerCarousel from "./BannerCarousel";
import LearningCarousel from "../../FunctionalComponents/Carousel/Carousel";
import ExploreLearningSection from "../HomePage/ExploreLearningSection";
import MasterSkillsSection from "../HomePage/MasterSkillsSection";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { syncUser } from "Redux/UserDetails";
import { getLearnerCourses } from "Redux/CourseDetails";
import {
  getCategoryPoints,
  fetchFoundationStatus,
  getFoundationReport,
} from "Redux/LearnersReport";
import IgniteLearnersSection from "../HomePage/IgniteLearnersSection";
import { Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import EventIcon from "@mui/icons-material/Event";
import useFetchImageUrls from "../../ConstantUseFetchImageUrls";
import { homePageAssets } from "../../imagesData/ImagesData";
import DialogPopup from "FunctionalComponents/DialogPopup";

const igniteTeamData = [
  {
    id: 1,
    name: "Saurabh Kaushik",
    role: "Core",
    image: "team1",
  },
  {
    id: 2,
    name: "Nisha Sharda",
    role: "Core",
    image: "team2",
  },
  {
    id: 3,
    name: "Sindhu Nambiar",
    role: "Core",
    image: "team3",
  },
  {
    id: 4,
    name: "Abhishek Jain",
    role: "Core",
    image: "team4",
  },
  {
    id: 5,
    name: "Mohana Krishnaveni",
    role: "Core",
    image: "team5",
  },
  {
    id: 6,
    name: "Avneesh Kaur G Bhatia",
    role: "Core",
    image: "team6",
  },
];

const NewHomePage = ({ openIvaPopup = () => {} }) => {
  //MyProfile Info
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const activeAccount = instance.getActiveAccount();
  const userData = useSelector((state) => state.userDetails.userData?.[0]);

  const userLoading = useSelector((state) => state.userDetails.loader);
  const coursesLoading = useSelector((state) => state.courseDetails.loader);
  const reportLoading = useSelector((state) => state.learnersReport.loader);

  const isPageLoading = userLoading || coursesLoading || reportLoading;

  const [showPopup, setShowPopup] = useState(false);
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const query = useMemo(
    () => `params=${encodeURIComponent(JSON.stringify(homePageAssets))}`,
    [],
  );
  const { imageUrls, error } = useFetchImageUrls(query);

  const urls = imageUrls?.imageUrl || {};
  const isBrowser = typeof window !== "undefined";

  useEffect(() => {
    if (!isBrowser) return;

    const alreadyShown = sessionStorage.getItem("igniteHomePopup");
    if (!alreadyShown) {
      setShowPopup(true);
      sessionStorage.setItem("igniteHomePopup", "true");
    }
  }, []);

  useEffect(() => {
    if (!isBrowser) return;

    const handleBeforeUnload = () => {
      sessionStorage.removeItem("igniteHomePopup");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isBrowser]);

  const closePopup = () => {
    setShowPopup(false);
  };

  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    if (!instance) {
      return;
    }

    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === "msal:loginSuccess" ||
        event.eventType === "msal:acquireTokenSuccess"
      ) {
        setTokenReady(true);
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;
  const name = activeAccount?.idTokenClaims?.name;
  const userLevelDes = activeAccount?.idTokenClaims?.jobFamilyDescription;
  const isEmpty = (value) =>
    value === null || value === undefined || value === "" || value === "NULL";
  const level = isEmpty(userLevelDes) ? userData?.level : userLevelDes;

  const role = activeAccount?.idTokenClaims?.jobTitle;
  const businessUnit = useMemo(
    () => userData?.businessUnit,
    [userData?.businessUnit],
  );

  useEffect(() => {
    if (!enterpriseId || !tokenReady) return;
    dispatch(syncUser(enterpriseId, name, level, role));
  }, [enterpriseId, tokenReady, name, level, role, dispatch]);

  useEffect(() => {
    if (!enterpriseId || !businessUnit) return;
    dispatch(getLearnerCourses(enterpriseId, businessUnit));
    dispatch(getCategoryPoints(enterpriseId, "ALL"));
    dispatch(fetchFoundationStatus(enterpriseId));
    dispatch(getFoundationReport());
  }, [enterpriseId, businessUnit, dispatch]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    if (!enterpriseId || isPageLoading) return;

    const key = `profileReminderHidden_${enterpriseId}`;

    const isProfileComplete =
      userData?.capability &&
      userData?.platform &&
      userData?.industry &&
      userData?.role;

    const isHidden = localStorage.getItem(key) === "true";

    if (!isHidden && !isProfileComplete && !dontShowAgain) {
      setShowProfileReminder(true);
    } else {
      setShowProfileReminder(false);
    }

    if ((dontShowAgain || isProfileComplete) && !isHidden) {
      localStorage.setItem(key, "true");
    }
  }, [enterpriseId, isPageLoading, userData, dontShowAgain]);

  const handleCloseProfileReminder = (event) => {
    event.stopPropagation();
    const key = `profileReminderHidden_${enterpriseId}`;
    localStorage.setItem(key, "true");
    setShowProfileReminder(false);
    setDontShowAgain(true);
  };

  if (error) return <div>Error loading assets</div>;
  if (!imageUrls || Object.keys(imageUrls).length === 0) return null;

  //MyProfile Info
  return (
    <>
      {isPageLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 20000,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      <div className="banner-carousel-box">
        <BannerCarousel />
        <div className="announcemnt-box">
          <p>
            <img src={urls["infoIcon"]} alt="info" className="speaker-img" />
            <marquee
              width="95%"
              direction="left"
              style={{
                display: "inline-block",
                verticalAlign: "middle",
                marginTop: "20px",
              }}
            >
              {/* "Exciting session on Digital Product Design by Thom Cowell
              scheduled for February 13th. A great opportunity to gain valuable
              insights and enhance you design expertise!" */}
              "Missed joining the Ignite Talks sessions? We have got you
              covered! The recordings from our recent masterclasses are now
              available for you to revisit and enjoy at your convenience."
            </marquee>
          </p>
        </div>
      </div>
      {/* start section */}
      <div className="about-ignite-section">
        <div className="about-ignite-wrap">
          <div className="about-ignite-row row1">
            <div className="about-ignite-box">
              <h2 className="section-headings">Inside Ignite</h2>
              <p>
                Ignite is a strategic learning initiative for Accenture Song
                employees, designed to build future-ready capabilities and
                enable industry-led digital customer technology transformation.
              </p>
              <p>
                Delivered through a unified digital platform, Ignite brings
                together curated Learning Hubs, structured learning journeys,
                and practical application. A built-in learning companion
                supports learners by guiding discovery, answering questions, and
                helping navigate learning journeys with ease.
              </p>
              <p>
                Learning begins with a Foundation experience and expands into
                deeper, role-relevant capabilities over time. Clear milestones
                and recognition make progress visible, keeping learning
                purposeful, continuous, and aligned to evolving business and
                delivery needs.
              </p>
              <p className="ask-iva-link explore-ignite">
                <a href="/aboutIgnite">
                  <ArrowForwardIcon /> Explore Ignite
                </a>
              </p>
            </div>
          </div>
          <div className="about-ignite-row row2">
            <div className="about-ignite-box">
              <video
                width="100%"
                height="100%"
                controls
                poster={urls["aboutPoster"]}
                preload="metadata"
                className="about-thumbnail"
              >
                <source src={urls["aboutVideo"]} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </div>
      {/* Update Profile Popup */}
      {showProfileReminder && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: "8px",
              width: "420px",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginBottom: "12px" }}>Configure your profile</h3>
            <p
              style={{ fontSize: "14px", marginBottom: "20px", color: "#555" }}
            >
              Configure your profile to get learning recommendations tailored to
              your role. Use the Profile icon in the top-right corner to update
              your details.
            </p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <input
                type="checkbox"
                id="dontShowAgain"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              <label htmlFor="dontShowAgain" style={{ fontSize: "13px" }}>
                I have updated the details. Don’t show this message again
              </label>
            </div>
            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <Button
                variant="contained"
                onClick={handleCloseProfileReminder}
                sx={{
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#080808" },
                }}
              >
                Setup Profile
              </Button>
              <Button variant="outlined" onClick={handleCloseProfileReminder}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* first section */}
      <div className="my-learning-section">
        <LearningCarousel />
      </div>
      {/* second section */}
      <div className="explore-learning-hub-section">
        <h2 className="section-headings">Explore Learning Hubs</h2>
        <a href="/learningHubs" className="learning-explore-link">
          <Button
            variant="contained"
            startIcon={<ArrowForwardIcon />}
            sx={{
              height: "2.7em",
              backgroundColor: "#000",
              "&:hover": {
                backgroundColor: "#080808",
              },
            }}
          >
            Explore All
          </Button>
        </a>
        <ExploreLearningSection />
      </div>
      {/* third section */}
      <div className="master-new-skills-section">
        <div className="master-new-skills-wrap">
          <MasterSkillsSection />
        </div>
      </div>
      {/* fifth section */}
      <div className="ignite-talks-section">
        <h2 className="section-headings">Ignite Talks</h2>
        <div className="ignite-talks-wrap">
          <img
            src={urls["igniteTalksImg"]}
            alt="ignite-talks-img"
            className="ignite-talks-img"
          />
          <div className="ignite-talks-content">
            <h4>Digital Customer</h4>
            <h3>Future of Digital Customer Experience </h3>
            {/* <p>
              Delve into the basics about AI. From how it can boost productivity
              to the types of AI to the widespread uses of AI.Delve into the
              basics about AI. From how it can boost productivity to the types
              of AI to the widespread uses of AI.
            </p> */}
            <p>
              This learning series explores how customer interactions are
              shifting from static, channel‑led designs to dynamic, contextual,
              multimodal, and agentic ecosystems. Experiences no longer stay
              still — they adapt continuously based on behavior, signals, data,
              and intent.
            </p>
            <p>
              <a href="/igniteTalks">
                <ArrowForwardIcon /> Watch Video
              </a>
            </p>
            <div className="video-details-box">
              <p>
                {/* <img
                  src={process.env.PUBLIC_URL + "/Assets/Home/hour-icon.png"}
                  alt="hour-icon"
                  className="hour-icon"
                /> */}
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <span>60 min</span>
              </p>
              <p>
                <VideoLibraryIcon sx={{ fontSize: 16 }} />
                <span>Recordings</span>
              </p>
              <p>
                {/* <img
                  src={
                    process.env.PUBLIC_URL + "/Assets/Home/calendar-icon.png"
                  }
                  alt="calendar-icon"
                  className="calendar-icon"
                /> */}
                <EventIcon sx={{ fontSize: 16 }} />
                <span>12-Dec-2025</span>
              </p>
              {/* <p>
                <img
                  src={process.env.PUBLIC_URL + "/Assets/Home/view-icon.png"}
                  alt="view-icon"
                  className="view-icon"
                />
                <span>12,435 views</span>
              </p> */}
            </div>
          </div>
        </div>
      </div>
      {/* sixth section */}
      <div className="ignite-virtual-assis-section">
        <div className="ignite-virtual-assis-wrap">
          <h2 className="section-headings">
            Meet IVA — the Ignite Virtual Assistant. <br />A learning companion
            always within your reach.
          </h2>
          <div className="iva-vertiual-text-img-wrap">
            <div className="iva-vertiual-text-img-box">
              <img
                src={urls["ivaIcon"]}
                alt="iva-img"
                className="iva-vertiual-text-icon"
              />
            </div>
            <div className="iva-vertiual-text-img-box">
              <img
                src={urls["ivaImage"]}
                alt="iva-img"
                className="iva-vertiual-text-img"
              />
            </div>
          </div>
          <p>
            IVA supports learning in the flow of work by answering questions,
            guiding exploration, and helping learners practice and progress
            across Learning Hubs.
          </p>
          <p className="ask-iva-link">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (typeof openIvaPopup === "function") {
                  openIvaPopup();
                }
              }}
            >
              <ArrowForwardIcon /> Start a Conversation / Ask IVA
            </a>
          </p>
        </div>
      </div>
      {/* seventh section */}
      <div className="ignite-learners-section">
        <h2 className="section-headings">Learners Leading this Week</h2>
        <p className="section-sub-text">
          ‘Recognized based on learning activity and credits earned during the
          current week.’ or ‘Celebrating consistent learning and progress’
        </p>
        <a href="/hallOfFame" className="learning-explore-link">
          <Button
            variant="contained"
            startIcon={<ArrowForwardIcon />}
            sx={{
              height: "2.7em",
              backgroundColor: "#000",
              "&:hover": {
                backgroundColor: "#080808",
              },
            }}
          >
            See All
          </Button>
        </a>
        <IgniteLearnersSection />
      </div>
      {/* eight section */}
      <div className="home-ignite-team-section">
        <div className="home-ignite-team-wrap">
          <div className="home-ignite-team-row">
            <div className="home-ignite-team-box">
              <p className="team-link">
                <a href="/team">
                  <img
                    src={urls["teamIcon"]}
                    alt="team-icon"
                    className="team-icon"
                  />{" "}
                  <span>TEAM</span>
                </a>
              </p>
              <h2 className="section-headings">Meet the Ignite Core Team</h2>
              <a href="/team" className="learning-explore-link">
                <Button
                  variant="contained"
                  startIcon={<ArrowForwardIcon />}
                  sx={{
                    height: "2.7em",
                    backgroundColor: "#000",
                    "&:hover": {
                      backgroundColor: "#080808",
                    },
                  }}
                >
                  See All
                </Button>
              </a>
            </div>
          </div>

          <div className="home-ignite-team-row">
            <div className="home-ignite-team-box">
              {igniteTeamData.map((member, index) => (
                <div className="home-ignite-team-content" key={member.id}>
                  <img
                    src={urls[member.image]}
                    alt={member.name}
                    className="core-profile-img"
                  />
                  <h3>{member.name}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* on load popup gif */}
      {/* {showPopup && (
        <div
          onClick={closePopup}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          className="onload-gif-row"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="onload-gif-box"
            style={{
              background: "#fff",
              position: "relative",
              textAlign: "center",
            }}
          >
            <button
              onClick={closePopup}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                background: "transparent",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>

            <img
              src={urls["popupGif"]}
              className="onload-gif"
              alt="IVA Intro"
              style={{
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
      )} */}

      <DialogPopup/>
      
    </>
  );
};

export default NewHomePage;
